import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useGamificationStore } from '@/stores/gamification';
import { useOnboardingStore } from '@/stores/onboarding';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Timer, X, Loader2 } from 'lucide-react';
import type { ApiResponse } from '@/types';

export function OnboardingChecklist() {
  const user = useAuthStore((s) => s.user);
  const data = useOnboardingStore((s) => s.payload);
  const loading = useOnboardingStore((s) => s.loading);
  const fetchOnboarding = useOnboardingStore((s) => s.fetch);
  const setPayload = useOnboardingStore((s) => s.setPayload);
  const resetOnboarding = useOnboardingStore((s) => s.reset);
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [savingDismiss, setSavingDismiss] = useState(false);
  const dontShowAgainRef = useRef(false);
  const closeInFlightRef = useRef(false);

  useEffect(() => {
    dontShowAgainRef.current = dontShowAgain;
  }, [dontShowAgain]);

  const completion = useMemo(() => {
    const tasks = data?.tasks ?? [];
    const completed = tasks.filter((t) => Boolean(t.progress?.completed_at)).length;
    return { completed, total: tasks.length };
  }, [data]);

  const load = useCallback(
    async (manualOpen = false) => {
      if (!user?.id) {
        resetOnboarding();
        setOpen(false);
        return;
      }
      try {
        await fetchOnboarding();
      } catch (e) {
        if (manualOpen) toast.error(e instanceof Error ? e.message : 'Onboarding konnte nicht geladen werden.');
        return;
      }
      const payload = useOnboardingStore.getState().payload;
      if (payload) {
        if (manualOpen) {
          setOpen(true);
          return;
        }
        if (!payload.modal_auto_open_suppressed) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [user?.id, fetchOnboarding, resetOnboarding]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    const handleOpenModal = () => {
      void load(true);
    };
    window.addEventListener('leftclick:onboarding-open-modal', handleOpenModal);
    return () => window.removeEventListener('leftclick:onboarding-open-modal', handleOpenModal);
  }, [load]);

  const toggleTask = async (taskId: string) => {
    try {
      const result = await api.patch<ApiResponse<unknown>>(`/onboarding/tasks/${taskId}/toggle`);
      if (!result.success) throw new Error(result.error || 'Aufgabe konnte nicht gespeichert werden.');
      await fetchOnboarding();
      if (user?.id) void useGamificationStore.getState().fetch(user.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aufgabe konnte nicht gespeichert werden.');
    }
  };

  const persistModalDismiss = async (): Promise<boolean> => {
    const templateId = data?.template.id;
    if (!dontShowAgainRef.current || !user?.id || !templateId) {
      return true;
    }
    setSavingDismiss(true);
    try {
      const res = await api.patch<ApiResponse<{ dismissed: boolean }>>('/onboarding/dismiss', {
        template_id: templateId,
      });
      if (!res.success) {
        toast.error(
          res.error ||
            'Einstellung konnte nicht gespeichert werden. Bitte Migration 025 in der Datenbank ausfuehren oder Administrator informieren.'
        );
        return false;
      }
      setPayload((prev) => (prev ? { ...prev, modal_auto_open_suppressed: true } : prev));
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
      return false;
    } finally {
      setSavingDismiss(false);
    }
  };

  const openTaskAction = async (path?: string) => {
    if (!path) return;
    const ok = await persistModalDismiss();
    if (!ok) return;
    window.location.hash = path.startsWith('/') ? path : `/${path}`;
    setOpen(false);
    setDontShowAgain(false);
  };

  const handleClose = async () => {
    if (closeInFlightRef.current) return;
    closeInFlightRef.current = true;
    try {
      const ok = await persistModalDismiss();
      if (!ok) return;

      setDontShowAgain(false);
      setOpen(false);
    } finally {
      closeInFlightRef.current = false;
    }
  };

  if (loading && !data) return null;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            void handleClose();
            return;
          }
          setOpen(true);
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{data?.template.name ?? 'Onboarding'}</DialogTitle>
            <DialogDescription>
              {completion.completed}/{completion.total} Punkte abgeschlossen
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {(data?.tasks ?? []).map((task) => (
              <div
                key={task.id}
                className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm"
              >
                <Checkbox
                  checked={Boolean(task.progress?.completed_at)}
                  onCheckedChange={() => toggleTask(task.id)}
                />
                <span className="flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{task.title}</span>
                    {task.category && <Badge variant="secondary">{task.category}</Badge>}
                    {task.estimated_minutes ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted">
                        <Timer className="h-3 w-3" />
                        {task.estimated_minutes} min
                      </span>
                    ) : null}
                  </span>
                  {task.description && (
                    <span className="mt-1 block text-xs text-muted">{task.description}</span>
                  )}
                  {task.help_text && (
                    <span className="mt-1 block text-xs text-muted">{task.help_text}</span>
                  )}
                  {task.action_path && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 px-2 text-xs"
                      onClick={() => openTaskAction(task.action_path)}
                    >
                      Jetzt oeffnen <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <label className="mr-auto flex items-center gap-2 text-xs text-muted">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              />
              Nicht mehr anzeigen
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleClose()}
              disabled={savingDismiss}
            >
              {savingDismiss ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  Schliessen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
